import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { DrupalRESTService } from './drupal-rest.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  /** The user's per-studio roles (empty if no assignments). */
  studioRoles: string[] = [];

  /** The user's per-studio permission keys for the current studio. */
  studioPermissions: string[] = [];

  /** Emits when studio permissions have been (re)loaded. */
  studioPermissionsLoaded = new BehaviorSubject<boolean>(false);

  /** True while the /my-permissions call is in flight. */
  loadingStudioPermissions = false;

  /** Studio ID from the last successful permissions load. */
  private _lastLoadedStudioId: string | null = null;

  // Legacy role-based ACL (Drupal roles → UI permissions).
  role_based_access_control_list = {
    administrator: {
      view_admin_links: true,
      manage_franchisee_role: true,
    },
    franchisee: {
      manage_franchisee_role: true,
    },
    authenticated: {
      "example": true
    }
  }

  constructor(
    public _authService: AuthService,
    private _drupalRESTService: DrupalRESTService,
  ) {
    // Reload studio permissions whenever the selected studio changes.
    this._authService.selectedStudioChanged.subscribe(() => {
      this.loadStudioPermissions();
    });
  }

  get_user_roles() {
    return this._authService.current_user?.['roles'];
  }

  /**
   * Returns true if the current user is a super-admin (uid 1 or 2658).
   */
  isSuperAdmin(): boolean {
    const uid = this._authService.current_user?.['uid'];
    const uidNum = typeof uid === 'string' ? parseInt(uid, 10) : uid;
    return uidNum === 1 || uidNum === 2658;
  }

  /**
   * Legacy permission check (Drupal role → hardcoded ACL).
   * Existing callers continue to work unchanged.
   */
  has_access(permission: string): boolean {
    // Super-admins (uid 1 / 2658) get the same admin-link access as the
    // Drupal `administrator` role, regardless of their assigned roles.
    if (permission === 'view_admin_links' && this.isSuperAdmin()) {
      return true;
    }
    // Check legacy ACL first.
    for (const role in this.get_user_roles()) {
      if (this.role_based_access_control_list?.[this.get_user_roles()[role]]?.[permission]) {
        return true;
      }
    }
    return false;
  }

  /**
   * Per-studio permission check.
   * Returns true if the current user has the given permission key
   * in the currently selected studio.
   */
  hasStudioPermission(permission: string): boolean {
    return this.studioPermissions.includes(permission);
  }

  /**
   * Whether studio permission enforcement is active for the current user.
   * Returns true only when the user has explicit studio role assignments.
   * Users with no assignments see everything (gradual rollout).
   */
  shouldEnforcePermissions(): boolean {
    return this.studioRoles.length > 0;
  }

  /**
   * Permission-gated access check for UI features.
   * If the user has no studio role assignments, returns true (unrestricted).
   * If the user has assignments, checks hasStudioPermission().
   * Admins (legacy ACL) always pass.
   */
  canAccess(permission: string): boolean {
    if (this.has_access('view_admin_links')) {
      return true;
    }
    if (!this.shouldEnforcePermissions()) {
      return true;
    }
    return this.hasStudioPermission(permission);
  }

  /**
   * Load the current user's role and permissions for the selected studio.
   * Called automatically when the studio changes.
   */
  loadStudioPermissions(force = false): void {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this._lastLoadedStudioId = null;
      this.studioRoles = [];
      this.studioPermissions = [];
      this.studioPermissionsLoaded.next(true);
      return;
    }

    // Skip if already loaded for this studio (unless forced).
    if (!force && studioId === this._lastLoadedStudioId) {
      return;
    }

    this.loadingStudioPermissions = true;

    this._drupalRESTService.httpGET(`/api/studio-permissions/studio/${studioId}/my-permissions`).subscribe({
      next: (res: any) => {
        if (res?.success && res.data) {
          this.studioRoles = res.data.roles || [];
          this.studioPermissions = res.data.permissions || [];
        } else {
          this.studioRoles = [];
          this.studioPermissions = [];
        }
        this._lastLoadedStudioId = studioId;
        this.loadingStudioPermissions = false;
        this.studioPermissionsLoaded.next(true);
      },
      error: () => {
        this.studioRoles = [];
        this.studioPermissions = [];
        this._lastLoadedStudioId = null;
        this.loadingStudioPermissions = false;
        this.studioPermissionsLoaded.next(true);
      },
    });
  }
}
